import type { McArtifact, McEvent, McFinding, McFindingDetail } from "../../lib/mc";

const record = (payload: unknown): Record<string, unknown> =>
  payload && typeof payload === "object" && !Array.isArray(payload) ? (payload as Record<string, unknown>) : {};

const text = (value: unknown): string | undefined => (typeof value === "string" && value ? value : undefined);

export interface McMission {
  id: string;
  kind?: string;
  objective: string;
  startedAt?: string;
  finishedAt?: string;
  /** Lifecycle status from mission.finished, e.g. "finished" */
  status?: string;
  outcome?: string;
  endReason?: string;
  /** The mission's closing report, without the "<outcome>: " prefix MC puts on the event summary */
  result?: string;
}

/** Missions in the order they started, from their mission.started and mission.finished events. */
export const deriveMissions = (events: McEvent[]): McMission[] => {
  const missions = new Map<string, McMission>();
  const missionFor = (id: string) => {
    let mission = missions.get(id);
    if (!mission) {
      mission = { id, objective: "" };
      missions.set(id, mission);
    }
    return mission;
  };
  for (const event of events) {
    if (!event.missionId) continue;
    const payload = record(event.payload);
    if (event.type === "mission.started") {
      const mission = missionFor(event.missionId);
      mission.objective = event.summary;
      mission.kind = text(payload.kind);
      mission.startedAt = event.at;
    } else if (event.type === "mission.finished") {
      const mission = missionFor(event.missionId);
      mission.finishedAt = event.at;
      mission.status = text(payload.status);
      mission.outcome = text(payload.outcome);
      mission.endReason = text(payload.endReason);
      const prefix = mission.outcome ? `${mission.outcome}: ` : "";
      mission.result = prefix && event.summary.startsWith(prefix) ? event.summary.slice(prefix.length) : event.summary;
    }
  }
  return Array.from(missions.values());
};

/** The run's findings, filled in with the detail from their `finding` events. */
export const findingDetails = (findings: McFinding[], events: McEvent[]): McFindingDetail[] => {
  const recorded = new Map<string, McFindingDetail>();
  for (const event of events) {
    if (event.type !== "finding") continue;
    const payload = record(event.payload);
    const id = text(payload.id);
    // `at` is the time the finding was recorded. A payload `recordedAt` still wins.
    if (id)
      recorded.set(id, {
        title: event.summary,
        missionId: event.missionId,
        recordedAt: event.at,
        ...payload,
        id,
      } as McFindingDetail);
  }
  const details: McFindingDetail[] = findings.map((finding) => ({ ...recorded.get(finding.id), ...finding }));
  recorded.forEach((detail, id) => {
    if (!findings.some((finding) => finding.id === id)) details.push(detail);
  });
  return details;
};

export interface McMemoryChange {
  seq: number;
  op: "add" | "update" | "remove";
  /** The entry's short id, for updates and removals */
  entryId?: string;
  text?: string;
  why?: string;
  missionId?: string;
}

/** Memory edits the run made, from its `memory.changed` events; they apply when the run finishes. */
export const memoryChanges = (events: McEvent[]): McMemoryChange[] =>
  events.flatMap((event) => {
    if (event.type !== "memory.changed") return [];
    const payload = record(event.payload);
    const op = payload.op;
    if (op !== "add" && op !== "update" && op !== "remove") return [];
    return [{ seq: event.seq, op, entryId: text(payload.id), text: text(payload.text), why: text(payload.why), missionId: event.missionId }];
  });

/** Artifact keys for a finding's evidence ids, falling back to the id when the artifact isn't listed. */
export const evidenceKeys = (finding: McFindingDetail, artifacts: McArtifact[]): string[] =>
  (Array.isArray(finding.evidenceArtifactIds) ? finding.evidenceArtifactIds : []).map(
    (id) => artifacts.find((artifact) => artifact.id === id)?.key ?? id
  );

/** `maxBrowsers` from run.started; runs started before MC reported it have none. */
export const maxBrowsers = (events: McEvent[]): number | undefined => {
  const value = record(events.find((event) => event.type === "run.started")?.payload).maxBrowsers;
  return typeof value === "number" ? value : undefined;
};

export interface EventFilter {
  id: string;
  label: string;
  matches: (type: string) => boolean;
}

const GROUPS: EventFilter[] = [
  { id: "lifecycle", label: "Run and missions", matches: (t) => /^(run|mission|guidance)\./.test(t) },
  { id: "tools", label: "Tools", matches: (t) => t.startsWith("tool.") },
  { id: "tool-errors", label: "Tool errors", matches: (t) => t === "tool.error" },
  { id: "model", label: "Model calls", matches: (t) => t === "model.call" },
  { id: "voice", label: "Voice", matches: (t) => t.startsWith("voice.") },
  { id: "findings", label: "Findings and notes", matches: (t) => ["finding", "observation", "uncertainty", "memory.changed"].includes(t) },
  { id: "waits", label: "Waits", matches: (t) => t.startsWith("wait.") },
];

const OTHER: EventFilter = {
  id: "other",
  label: "Other",
  matches: (t) => !GROUPS.some((group) => group.id !== "tool-errors" && group.matches(t)),
};

export const ALL_EVENTS: EventFilter = { id: "all", label: "All events", matches: () => true };

/** "All events" plus each group that has events, with counts for the filter's labels. */
export const eventFilters = (events: McEvent[]): (EventFilter & { count: number })[] =>
  [ALL_EVENTS, ...GROUPS, OTHER]
    .map((filter) => ({ ...filter, count: events.filter((event) => filter.matches(event.type)).length }))
    .filter((filter) => filter.id === ALL_EVENTS.id || filter.count > 0);
